/**
 * SettingsScreen - Detailed settings pages
 * 
 * Handles: Subscription, Privacy, Security, Language
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import {
  usePreferences,
  type UserPreferences,
  type UpdatablePreferenceKey,
} from '../services/preferencesApi';
import {
  SpaceBackground,
  GlassCard,
  AtomIcon,
  Header,
  Sidebar,
  SettingsPanel,
  BottomNav,
  ChatBar,
} from '../components/shared';
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  Globe,
  Lock,
  Eye,
  Check,
  Smartphone,
  Key,
  Bell,
  Moon,
  Sun,
  Crown,
  Star,
  Loader2,
} from 'lucide-react';

type SettingsType = 'subscription' | 'privacy' | 'security' | 'language';

// ============================================================================
// Shared toggle component
// ============================================================================

interface ToggleSwitchProps {
  enabled: boolean;
  onToggle: () => void;
}

const ToggleSwitch = ({ enabled, onToggle }: ToggleSwitchProps) => (
  <button
    onClick={onToggle}
    className={`w-14 h-8 rounded-full transition-all ${enabled ? 'bg-cyan-400' : 'bg-cyan-400/20'}`}
  >
    <div
      className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-7' : 'translate-x-1'}`}
    />
  </button>
);

// ============================================================================
// Subscription View — shows current plan from Firestore, upgrade buttons disabled
// ============================================================================

interface SubscriptionViewProps {
  currentPlan: UserPreferences['plan'];
}

const SubscriptionView = ({ currentPlan }: SubscriptionViewProps) => {
  const plans = [
    {
      id: 'free' as const,
      name: 'Free',
      price: '$0',
      features: ['Basic AI chat', '100 messages/day', 'Standard response time'],
    },
    {
      id: 'pro' as const,
      name: 'Pro',
      price: '$20/mo',
      features: ['Unlimited messages', 'Fast responses', 'Voice input', 'Basic integrations'],
    },
    {
      id: 'super_pro' as const,
      name: 'Super Pro',
      price: '$50/mo',
      features: [
        'Everything in Pro',
        'God Mode (App Building)',
        'All integrations',
        'Priority support',
        'Custom AI personas',
      ],
    },
    {
      id: 'enterprise' as const,
      name: 'Enterprise',
      price: 'Custom',
      features: [
        'Everything in Super Pro',
        'Dedicated support',
        'Custom deployments',
        'SLA guarantees',
      ],
    },
  ];

  const currentPlanInfo = plans.find((p) => p.id === currentPlan) || plans[0];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-cyan-400/20 to-purple-400/20 rounded-xl border border-cyan-400/30">
          <Crown className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-cyan-300">Subscription</h2>
          <p className="text-sm text-cyan-400/60">Manage your plan</p>
        </div>
      </div>

      {/* Current Plan Badge */}
      <GlassCard className="p-4 border-cyan-400/50 bg-gradient-to-r from-cyan-400/10 to-purple-400/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Star className="w-5 h-5 text-yellow-400" />
            <span className="text-cyan-300 font-semibold">
              Current Plan: {currentPlanInfo.name}
            </span>
          </div>
          <span className="text-cyan-400/70">{currentPlanInfo.price}</span>
        </div>
      </GlassCard>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((plan) => {
          const isCurrent = plan.id === currentPlan;
          return (
            <GlassCard
              key={plan.id}
              className={`p-5 ${isCurrent ? 'border-cyan-400/50 ring-2 ring-cyan-400/30 bg-gradient-to-br from-cyan-400/5 to-purple-400/5' : ''}`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-bold text-cyan-300">{plan.name}</h3>
                {isCurrent && (
                  <span className="px-2 py-1 text-xs bg-cyan-400/20 text-cyan-400 rounded-full">
                    Current
                  </span>
                )}
              </div>
              <p className="text-2xl font-bold text-cyan-400 mb-4">{plan.price}</p>
              <ul className="space-y-2">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-center space-x-2 text-sm text-cyan-400/70">
                    <Check className="w-4 h-4 text-green-400" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              {!isCurrent && (
                <button
                  disabled
                  className="w-full mt-4 py-2 bg-cyan-400/5 border border-cyan-400/20 rounded-xl text-cyan-400/40 font-semibold cursor-not-allowed"
                >
                  Coming soon
                </button>
              )}
            </GlassCard>
          );
        })}
      </div>
    </div>
  );
};

// ============================================================================
// Security View — 2FA toggle persisted, password reset via Firebase Auth
// ============================================================================

interface SecurityViewProps {
  twoFactorEnabled: boolean;
  onToggle2FA: (value: boolean) => void;
  onPasswordReset: () => Promise<void>;
  authProvider: 'google' | 'email' | 'unknown';
  userEmail: string | null;
}

const SecurityView = ({
  twoFactorEnabled,
  onToggle2FA,
  onPasswordReset,
  authProvider,
  userEmail,
}: SecurityViewProps) => {
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState<string | null>(null);

  const handlePasswordReset = async () => {
    try {
      setPasswordResetError(null);
      await onPasswordReset();
      setPasswordResetSent(true);
    } catch {
      setPasswordResetError('Failed to send password reset email. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 bg-cyan-400/10 rounded-xl border border-cyan-400/30">
          <Shield className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-cyan-300">Security</h2>
          <p className="text-sm text-cyan-400/60">Protect your account</p>
        </div>
      </div>

      {/* Two-Factor Auth */}
      <GlassCard className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Key className="w-5 h-5 text-cyan-400" />
            <div>
              <h3 className="font-semibold text-cyan-300">Two-Factor Authentication</h3>
              <p className="text-sm text-cyan-400/60">Add an extra layer of security</p>
            </div>
          </div>
          <ToggleSwitch enabled={twoFactorEnabled} onToggle={() => onToggle2FA(!twoFactorEnabled)} />
        </div>
        {twoFactorEnabled && (
          <div className="mt-4 p-3 bg-green-400/10 border border-green-400/30 rounded-xl">
            <div className="flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-sm text-green-400">2FA is enabled via authenticator app</span>
            </div>
          </div>
        )}
      </GlassCard>

      {/* Password */}
      <GlassCard className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Lock className="w-5 h-5 text-cyan-400" />
            <div>
              <h3 className="font-semibold text-cyan-300">Password</h3>
              {authProvider === 'google' ? (
                <p className="text-sm text-cyan-400/60">
                  Signed in with Google &mdash; manage via your Google account
                </p>
              ) : (
                <p className="text-sm text-cyan-400/60">
                  {userEmail ? `Send a reset link to ${userEmail}` : 'Manage your password'}
                </p>
              )}
            </div>
          </div>
          {authProvider !== 'google' && (
            <button
              onClick={handlePasswordReset}
              disabled={passwordResetSent}
              className={`px-4 py-2 border rounded-xl transition-all ${
                passwordResetSent
                  ? 'bg-green-400/10 border-green-400/30 text-green-400 cursor-default'
                  : 'bg-cyan-400/10 hover:bg-cyan-400/20 border-cyan-400/30 text-cyan-400'
              }`}
            >
              {passwordResetSent ? 'Email Sent' : 'Change'}
            </button>
          )}
        </div>
        {passwordResetError && (
          <p className="mt-2 text-sm text-red-400">{passwordResetError}</p>
        )}
      </GlassCard>

      {/* Active Sessions */}
      <div className="mt-6">
        <h3 className="text-sm font-semibold text-cyan-400/50 uppercase tracking-wider mb-4">
          Active Sessions
        </h3>
        <GlassCard className="divide-y divide-cyan-400/10">
          {[{ device: 'This Browser', location: 'Current session', current: true }].map(
            (session, i) => (
              <div key={i} className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <Smartphone className="w-5 h-5 text-cyan-400/60" />
                  <div>
                    <p className="text-cyan-300">{session.device}</p>
                    <p className="text-sm text-cyan-400/50">{session.location}</p>
                  </div>
                </div>
                {session.current && (
                  <span className="px-2 py-1 text-xs bg-green-400/20 text-green-400 rounded-full">
                    Current
                  </span>
                )}
              </div>
            ),
          )}
        </GlassCard>
      </div>
    </div>
  );
};

// ============================================================================
// Language View — language, response language, and theme persisted to Firestore
// ============================================================================

interface LanguageViewProps {
  language: string;
  responseLanguage: string;
  theme: 'dark' | 'light' | 'system';
  onUpdatePref: <K extends UpdatablePreferenceKey>(
    key: K,
    value: UserPreferences[K],
  ) => Promise<void>;
}

const LanguageView = ({ language, responseLanguage, theme, onUpdatePref }: LanguageViewProps) => {
  const languages = [
    { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
    { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 bg-cyan-400/10 rounded-xl border border-cyan-400/30">
          <Globe className="w-6 h-6 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-cyan-300">Language &amp; Display</h2>
          <p className="text-sm text-cyan-400/60">Customize your experience</p>
        </div>
      </div>

      {/* Theme */}
      {/* Theme — Dark mode only for launch */}
      <GlassCard className="p-5">
        <h3 className="font-semibold text-cyan-300 mb-4">Theme</h3>
        <div className="grid grid-cols-3 gap-3">
          {([
            { id: 'light' as const, name: 'Glass', icon: Sun, disabled: true },
            { id: 'dark' as const, name: 'Dark', icon: Moon, disabled: false },
            { id: 'system' as const, name: 'System', icon: Smartphone, disabled: true },
          ]).map((t) => (
            <button
              key={t.id}
              onClick={() => !t.disabled && onUpdatePref('theme', t.id)}
              disabled={t.disabled}
              title={t.disabled ? 'Coming soon' : undefined}
              className={`p-4 rounded-xl border transition-all ${
                t.disabled
                  ? 'opacity-50 cursor-not-allowed bg-cyan-400/5 border-cyan-400/20'
                  : theme === t.id
                    ? 'bg-cyan-400/20 border-cyan-400/50'
                    : 'bg-cyan-400/5 border-cyan-400/20 hover:border-cyan-400/40'
              }`}
            >
              <t.icon
                className={`w-6 h-6 mx-auto mb-2 ${!t.disabled && theme === t.id ? 'text-cyan-400' : 'text-cyan-400/60'}`}
              />
              <p className={`text-sm ${!t.disabled && theme === t.id ? 'text-cyan-300' : 'text-cyan-400/60'}`}>
                {t.name}
              </p>
              {t.disabled && (
                <p className="text-[10px] text-cyan-400/40 mt-1">Coming soon</p>
              )}
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Interface Language */}
      <GlassCard className="p-5">
        <h3 className="font-semibold text-cyan-300 mb-4">Interface Language</h3>
        <div className="space-y-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => onUpdatePref('language', lang.code)}
              className={`w-full p-3 rounded-xl border transition-all flex items-center justify-between ${
                language === lang.code
                  ? 'bg-cyan-400/20 border-cyan-400/50'
                  : 'bg-cyan-400/5 border-cyan-400/20 hover:border-cyan-400/40'
              }`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{lang.flag}</span>
                <span className={language === lang.code ? 'text-cyan-300' : 'text-cyan-400/70'}>
                  {lang.name}
                </span>
              </div>
              {language === lang.code && <Check className="w-5 h-5 text-cyan-400" />}
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Response Language */}
      <GlassCard className="p-5">
        <h3 className="font-semibold text-cyan-300 mb-4">AI Response Language</h3>
        <p className="text-sm text-cyan-400/60 mb-4">
          Choose the language Jarvis responds in
        </p>
        <div className="space-y-2">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => onUpdatePref('responseLanguage', lang.code)}
              className={`w-full p-3 rounded-xl border transition-all flex items-center justify-between ${
                responseLanguage === lang.code
                  ? 'bg-cyan-400/20 border-cyan-400/50'
                  : 'bg-cyan-400/5 border-cyan-400/20 hover:border-cyan-400/40'
              }`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{lang.flag}</span>
                <span
                  className={responseLanguage === lang.code ? 'text-cyan-300' : 'text-cyan-400/70'}
                >
                  {lang.name}
                </span>
              </div>
              {responseLanguage === lang.code && <Check className="w-5 h-5 text-cyan-400" />}
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Notifications (placeholder) */}
      <GlassCard className="p-5" onClick={() => alert('Push notification settings coming soon')}>
        <div className="flex items-center justify-between cursor-pointer">
          <div className="flex items-center space-x-3">
            <Bell className="w-5 h-5 text-cyan-400" />
            <div>
              <h3 className="font-semibold text-cyan-300">Push Notifications</h3>
              <p className="text-sm text-cyan-400/60">Get notified about important updates</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-cyan-400/40" />
        </div>
      </GlassCard>
    </div>
  );
};

// ============================================================================
// Privacy View — data sharing and analytics toggles persisted to Firestore
// ============================================================================

interface PrivacyViewProps {
  dataSharing: boolean;
  analyticsOptIn: boolean;
  onToggleDataSharing: (value: boolean) => void;
  onToggleAnalytics: (value: boolean) => void;
}

const PrivacyView = ({
  dataSharing,
  analyticsOptIn,
  onToggleDataSharing,
  onToggleAnalytics,
}: PrivacyViewProps) => (
  <div className="space-y-6">
    <div className="flex items-center space-x-3 mb-6">
      <div className="p-3 bg-cyan-400/10 rounded-xl border border-cyan-400/30">
        <Eye className="w-6 h-6 text-cyan-400" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-cyan-300">Privacy</h2>
        <p className="text-sm text-cyan-400/60">Control your data</p>
      </div>
    </div>

    {/* Data Sharing */}
    <GlassCard className="p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div>
            <h3 className="font-semibold text-cyan-300">Data Sharing</h3>
            <p className="text-sm text-cyan-400/60">
              Share anonymized usage data to improve SuperJarvis
            </p>
          </div>
        </div>
        <ToggleSwitch enabled={dataSharing} onToggle={() => onToggleDataSharing(!dataSharing)} />
      </div>
    </GlassCard>

    {/* Analytics */}
    <GlassCard className="p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div>
            <h3 className="font-semibold text-cyan-300">Analytics</h3>
            <p className="text-sm text-cyan-400/60">Help us understand how you use the app</p>
          </div>
        </div>
        <ToggleSwitch
          enabled={analyticsOptIn}
          onToggle={() => onToggleAnalytics(!analyticsOptIn)}
        />
      </div>
    </GlassCard>

    {/* Danger Zone */}
    <GlassCard className="p-5 border-red-400/30">
      <h3 className="font-semibold text-red-400 mb-2">Danger Zone</h3>
      <p className="text-sm text-cyan-400/60 mb-4">
        Once you delete your data, there is no going back.
      </p>
      <div className="flex space-x-3">
        <button onClick={() => alert('Data export will be available soon. Contact support@superjarvis.ai for immediate requests.')} className="px-4 py-2 bg-red-400/10 hover:bg-red-400/20 border border-red-400/30 rounded-xl text-red-400 transition-all">
          Export Data
        </button>
        <button onClick={() => { if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) { alert('Account deletion request submitted. You will receive a confirmation email.'); } }} className="px-4 py-2 bg-red-400/20 hover:bg-red-400/30 border border-red-400/50 rounded-xl text-red-400 transition-all">
          Delete Account
        </button>
      </div>
    </GlassCard>
  </div>
);

// ============================================================================
// Loading Skeleton
// ============================================================================

const SettingsLoading = () => (
  <div className="flex flex-col items-center justify-center py-24 space-y-4">
    <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
    <p className="text-cyan-400/60 text-sm">Loading preferences...</p>
  </div>
);

// Main SettingsScreen
export default function SettingsScreen() {
  const { type } = useParams<{ type: SettingsType }>();
  const navigate = useNavigate();
  const { user, signOut } = useAuthContext();
  const { preferences, loading, updatePref, resetPassword, authProvider } = usePreferences();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inputText, setInputText] = useState('');
  const [chatMode, setChatMode] = useState('Super');

  const handleSend = () => {
    if (inputText.trim()) {
      navigate(`/?message=${encodeURIComponent(inputText.trim())}`);
      setInputText('');
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'subscription': return 'Subscription';
      case 'privacy': return 'Privacy';
      case 'security': return 'Security';
      case 'language': return 'Language & Display';
      default: return 'Settings';
    }
  };

  const renderContent = () => {
    if (loading || !preferences) {
      return <SettingsLoading />;
    }

    switch (type) {
      case 'subscription':
        return <SubscriptionView currentPlan={preferences.plan} />;
      case 'privacy':
        return (
          <PrivacyView
            dataSharing={preferences.dataSharing}
            analyticsOptIn={preferences.analyticsOptIn}
            onToggleDataSharing={(v) => updatePref('dataSharing', v)}
            onToggleAnalytics={(v) => updatePref('analyticsOptIn', v)}
          />
        );
      case 'security':
        return (
          <SecurityView
            twoFactorEnabled={preferences.twoFactorEnabled}
            onToggle2FA={(v) => updatePref('twoFactorEnabled', v)}
            onPasswordReset={resetPassword}
            authProvider={authProvider}
            userEmail={user?.email ?? null}
          />
        );
      case 'language':
        return (
          <LanguageView
            language={preferences.language}
            responseLanguage={preferences.responseLanguage}
            theme={preferences.theme}
            onUpdatePref={updatePref}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative w-full h-screen overflow-hidden bg-black text-cyan-400 font-sans">
      <SpaceBackground />

      <div className={`relative z-10 flex flex-col h-full transition-transform duration-500 ${sidebarOpen ? 'translate-x-[320px] scale-95 opacity-50 blur-[2px]' : ''}`}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        {/* Content */}
        <div className="flex-1 overflow-y-auto pt-24 px-4 pb-60">
          <div className="max-w-2xl mx-auto">
            {/* Back + Title */}
            <div className="flex items-center space-x-4 mb-6">
              <button
                onClick={() => navigate(-1)}
                className="p-3 rounded-full bg-cyan-500/[0.05] hover:bg-cyan-400/[0.1] border border-cyan-400/30 transition-all"
              >
                <ChevronLeft className="w-5 h-5 text-cyan-400" />
              </button>
              <div className="flex items-center space-x-3">
                <AtomIcon className="w-8 h-8 text-cyan-400 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)] animate-spin-slow" />
                <h1 className="text-2xl font-bold text-cyan-400">{getTitle()}</h1>
              </div>
            </div>

            {renderContent()}
          </div>
        </div>

        {/* Bottom */}
        <div className="absolute bottom-0 w-full flex flex-col items-center z-30 px-4 pb-4 bg-gradient-to-t from-black via-black/95 to-transparent pt-16">
          <ChatBar
            inputText={inputText}
            setInputText={setInputText}
            chatMode={chatMode}
            setChatMode={setChatMode}
            onSend={handleSend}
          />
          <BottomNav activeTab="recent" />
        </div>
      </div>

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSettingsClick={() => { setSidebarOpen(false); setSettingsOpen(true); }}
        userName={user?.displayName || user?.email?.split('@')[0]}
        userPhoto={user?.photoURL}
      />
      <SettingsPanel isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} onSignOut={signOut} />
    </div>
  );
}
